import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { IUpdateConfigPartial } from 'tag/ts/tag/interfaces/api/IUpdateConfigPartial';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IConfig } from 'tag/ts/tag/interfaces/config/IConfig';
import { IConfigUpdatedEventData } from 'tag/ts/tag/interfaces/events/IConfigUpdatedEventData';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Backs tag.API.TAG.updateConfig(). Validates an IUpdateConfigPartial against an
 * explicit dot-path whitelist, rejects unsafe leaf values (DOM nodes, function refs, non-plain
 * objects, cyclic references), deep-merges into tag.model.config, and dispatches
 * TagEvent.ON_CONFIG_UPDATED with a per-path before/after diff.
 *
 * The whitelist is the contract: extending it is an intentional act, not an emergent one.
 * Validation is all-or-nothing — any failure throws before tag.model.config is mutated.
 *
 * Effective on next player rebuild — Smart Tag's avia integration does not support plugin
 * reconfiguration on a live player instance, so plugin shouldBeUsed() re-evaluation only
 * happens during the next #atomicPlayerReplacement (i.e. next tag.API.VIDEO.load()).
 */
export class UpdateConfigService extends AbstractTagClass {

	/**
	 * @description Whitelist of safe-to-mutate dot-paths. Extending this set is a public
	 * commitment that the path is safe to swap on a live tag.model.config and that the
	 * next #atomicPlayerReplacement will pick it up.
	 *
	 * Currently scoped to the only proven shouldBeUsed()-gated skin-config field today:
	 * skin.PauseAd.enabled (read by PauseAdsPlugin.shouldBeUsed()).
	 */
	static readonly #SAFE_PATHS: ReadonlySet<string> = new Set<string>( [
		'skin.PauseAd.enabled'
	] );

	static readonly #TOP_LEVEL_KEYS: ReadonlySet<string> = new Set<string>( [ 'tag', 'skin' ] );

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'UpdateConfigService'
			}
		);
	}

	/**
	 * @description Public entry point. Validates the partial, snapshots before-state for the
	 * touched paths, deep-merges into tag.model.config, computes the diff, dispatches
	 * TagEvent.ON_CONFIG_UPDATED, and resolves with the diff payload.
	 *
	 * **Error model (intentional):** programmer errors throw *synchronously* — bad input
	 * (off-whitelist path, DOM/function/non-plain/cyclic value) and "Tag not initialized"
	 * are surfaced as sync throws so they're visible at the call site even if the caller
	 * forgets `await`. The Promise return type is reserved for genuine async failures
	 * future implementations may introduce (e.g. schema fetch, partial-rebuild coordination).
	 * This is the `fetch()` pattern: `fetch(undefined)` throws TypeError synchronously;
	 * network failures come back as rejections.
	 *
	 * Not declared `async` on purpose — the `async` keyword would convert sync throws into
	 * rejections, which is what we're avoiding.
	 *
	 * @param partial - Whitelisted partial to merge into tag.model.config.
	 * @returns Diff payload listing changed dot-paths with per-path before/after values.
	 *          Same shape as the TagEvent.ON_CONFIG_UPDATED event data.
	 */
	updateConfig = ( partial: IUpdateConfigPartial ): Promise<IConfigUpdatedEventData> => {
		this?.logger?.log( 'updateConfig:', partial );

		const leafPaths = this.#validate( partial );

		const config = this.tag?.model?.config;
		if ( !this.utils.Type.isObject( config ) ) {
			throw new Error( 'updateConfig: tag.model.config is not available — Tag is not initialized.' );
		}

		const before = this.#snapshotPaths( config, leafPaths );

		// DeepMerge mutates and returns target; tag.model.config getter returns the live
		// reference so the merge persists for plugin shouldBeUsed() on next rebuild.
		this.utils.DeepMerge.deepmerge( config, partial );

		const eventData = this.#computeDiff( before, config, leafPaths );

		const tagEventData = this.getTagEventDataVO();
		tagEventData.configUpdated = eventData;
		this.dispatchTagEvent(
			this.getTagEventVO( TagEvent.ON_CONFIG_UPDATED, this.tag, tagEventData, this, this.name )
		);

		return Promise.resolve( eventData );
	};

	/**
	 * @description Validates the partial. Throws on any failure; never mutates the input.
	 *
	 * Order of checks:
	 *  1. Top-level shape — plain object with at least one of `tag` / `skin`.
	 *  2. Walk the tree — collect leaf dot-paths and enforce per-value safety
	 *     (DOM, function, non-plain object, cyclic ref) along the way.
	 *  3. Whitelist — every leaf path must be in #SAFE_PATHS.
	 *
	 * @returns Flat list of leaf dot-paths the partial touches (subset of #SAFE_PATHS).
	 */
	#validate = ( partial: IUpdateConfigPartial ): string[] => {
		if ( !this.utils.Type.isObject( partial ) || this.utils.Type.isArray( partial ) ) {
			throw new Error( 'updateConfig: partial must be a plain object with `tag` and/or `skin` keys.' );
		}

		const topKeys = Object.keys( partial );
		if ( topKeys.length === 0 ) {
			throw new Error( 'updateConfig: partial is empty — at least one of `tag` or `skin` must be provided.' );
		}
		for ( const key of topKeys ) {
			if ( !UpdateConfigService.#TOP_LEVEL_KEYS.has( key ) ) {
				throw new Error(
					`updateConfig: top-level key "${key}" is not supported. ` +
					'Only "tag" and "skin" are accepted; CONTENT bucket is per-load by design.'
				);
			}
		}

		const leafPaths: string[] = [];
		const visited = new WeakSet<object>();
		for ( const key of topKeys ) {
			this.#walk( partial[ key as 'tag' | 'skin' ], key, leafPaths, visited );
		}

		for ( const path of leafPaths ) {
			if ( !UpdateConfigService.#SAFE_PATHS.has( path ) ) {
				throw new Error(
					`updateConfig: path "${path}" is not in the safe-path whitelist. ` +
					`Allowed paths: ${Array.from( UpdateConfigService.#SAFE_PATHS ).join( ', ' )}.`
				);
			}
		}

		return leafPaths;
	};

	/**
	 * @description Recursively walks a partial subtree, recording every leaf dot-path and
	 * enforcing per-value safety (DOM/function/non-plain-object/cyclic) along the way.
	 *
	 * A "leaf" is anything that isn't a plain `{}` object — primitives, null, undefined,
	 * arrays. Whitelist entries name leaves (e.g. 'skin.PauseAd.enabled'), not branches.
	 */
	#walk = (
		node: unknown,
		path: string,
		leafPaths: string[],
		visited: WeakSet<object>
	): void => {
		// DOM check first — Type.isElement covers Element/HTMLElement/Document/DocumentFragment
		// (anything with a callable appendChild). Window has no appendChild and is caught later
		// by the non-plain-object prototype check.
		if ( this.utils.Type.isElement( node ) ) {
			throw new Error(
				`updateConfig: value at path "${path}" is a DOM-like object. ` +
				'DOM references are forbidden in config partials — they cannot safely deep-merge.'
			);
		}

		if ( this.utils.Type.isFunction( node ) ) {
			throw new Error(
				`updateConfig: value at path "${path}" is a function. Function refs are forbidden in ` +
				'config partials — TAG.callback / TAG.namespace.* method tables are out of scope for this API.'
			);
		}

		// Primitives, null, undefined are valid leaves. Arrays are also treated as leaves —
		// the whitelist names a specific dot-path, and array deep-merge has its own surprises
		// (union-merge by index) we don't want to expose implicitly until forced by a use case.
		if ( !this.utils.Type.isObject( node ) || this.utils.Type.isArray( node ) ) {
			leafPaths.push( path );
			return;
		}

		// Non-plain-object filter — catches Map/Set/Promise/Date/RegExp/class instances/Window.
		// Type.isObject() accepts anything with typeof 'object', which is too permissive here;
		// the prototype check is what enforces "plain {} only". Object.getPrototypeOf is ES5
		// native (already used in Type.isBlankObject) so no transpilation concerns.
		const proto = Object.getPrototypeOf( node );
		if ( proto !== Object.prototype && proto !== null ) {
			throw new Error(
				`updateConfig: value at path "${path}" is a non-plain object ` +
				`(${Object.prototype.toString.call( node )}). Map/Set/Promise/Date/RegExp/class instances ` +
				'are forbidden in config partials.'
			);
		}

		if ( visited.has( node as object ) ) {
			throw new Error(
				`updateConfig: cyclic reference detected at path "${path}". Config partials must be acyclic.`
			);
		}
		visited.add( node as object );

		const childKeys = Object.keys( node as object );
		// Empty plain object has no leaves to whitelist-check — treat the parent as a leaf
		// so it gets caught by the whitelist check rather than silently accepted as a no-op.
		if ( childKeys.length === 0 ) {
			leafPaths.push( path );
			return;
		}

		for ( const childKey of childKeys ) {
			const child = ( node as { [ key: string ]: unknown } )[ childKey ];
			this.#walk( child, `${path}.${childKey}`, leafPaths, visited );
		}
	};

	/**
	 * @description Snapshots the current value at each dot-path in `paths` from the IConfig.
	 */
	#snapshotPaths = ( source: IConfig, paths: string[] ): { [ path: string ]: unknown } => {
		const snapshot: { [ path: string ]: unknown } = {};
		for ( const path of paths ) {
			snapshot[ path ] = this.#readPath( source, path );
		}
		return snapshot;
	};

	/**
	 * @description Computes the per-path before/after diff between a pre-merge snapshot and
	 * the post-merge IConfig. Paths whose values are unchanged (===) are omitted.
	 */
	#computeDiff = (
		before: { [ path: string ]: unknown },
		after: IConfig,
		paths: string[]
	): IConfigUpdatedEventData => {
		const changes: { [ path: string ]: { from: unknown; to: unknown } } = {};
		const changedPaths: string[] = [];

		for ( const path of paths ) {
			const fromValue = before[ path ];
			const toValue = this.#readPath( after, path );
			if ( fromValue !== toValue ) {
				changes[ path ] = { from: fromValue, to: toValue };
				changedPaths.push( path );
			}
		}

		return { paths: changedPaths, changes };
	};

	/**
	 * @description Reads a dot-path's leaf value from a config snapshot. Returns undefined
	 * if any segment along the path is missing.
	 */
	#readPath = ( source: unknown, path: string ): unknown => {
		const segments = path.split( '.' );
		let cursor: unknown = source;
		for ( const segment of segments ) {
			if ( !this.utils.Type.isObject( cursor ) ) {
				return undefined;
			}
			cursor = ( cursor as { [ key: string ]: unknown } )[ segment ];
		}
		return cursor;
	};

}